import React, { useEffect } from 'react';
import { Plus, Edit, Trash, Wallet, TrendingUp } from 'lucide-react';
import { useMovements } from '../hooks/useMovements';

const MovementsSection = ({ themes, currentTheme, services, clients }) => {
  const {
    movements,
    loading,
    error,
    setError,
    showMovementForm,
    setShowMovementForm,
    newMovement,
    setNewMovement,
    monthFilter,
    setMonthFilter,
    yearFilter,
    setYearFilter,
    fetchMovements,
    handleSaveMovement,
    handleDeleteMovement,
    handleEditMovement,
    getFilteredMovements,
    calculateSalesStats,
    calculateTotalSales
  } = useMovements();

  useEffect(() => {
    fetchMovements();
  }, []);

  const getClientName = (clientId) => {
    if (!clientId) return 'N/A';
    const client = clients.find(c => c.id === parseInt(clientId));
    return client ? client.name : 'N/A';
  };

  const getServiceName = (serviceId) => {
    if (!serviceId) return 'N/A';
    const service = services.find(s => s.id === parseInt(serviceId));
    return service ? service.name : 'N/A';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading && movements.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin mx-auto text-blue-500" />
          <p className={`mt-2 ${themes[currentTheme].textMuted}`}>Cargando movimientos...</p>
        </div>
      </div>
    );
  }

  if (error && movements.length === 0) {
    return (
      <div className="p-6 bg-red-900 bg-opacity-25 rounded-lg">
        <p className="text-red-500">{error}</p>
        <button 
          className="mt-2 px-4 py-2 bg-blue-600 rounded-md text-white"
          onClick={() => {
            setError(null);
            fetchMovements();
          }}
        >
          Intentar de nuevo
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <h1 className={`text-2xl font-bold ${themes[currentTheme].textPrimary}`}>Listado de Movimientos</h1>
          <div className="ml-4 flex items-center bg-green-900 bg-opacity-30 p-2 rounded-lg">
            <Wallet size={24} className="text-green-400 mr-2" />
            <div>
              <p className="text-xs text-gray-400">Total ventas</p>
              <p className="text-xl font-bold text-green-400">{formatCurrency(calculateTotalSales())}</p>
            </div>
          </div>
        </div>
        <button 
          className="bg-green-500 text-white px-4 py-2 rounded-md flex items-center gap-2"
          onClick={() => {
            setNewMovement({ 
              date: new Date().toISOString().slice(0, 10), 
              type: 'venta', 
              amount: 0, 
              clientId: clients.length > 0 ? clients[0].id : '', 
              serviceId: services.length > 0 ? services[0].id : '', 
              description: '' 
            });
            setShowMovementForm(true);
          }}
        >
          <Plus size={20} /> Nuevo
        </button>
      </div>
      
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-[300px]">
          <div className={`${themes[currentTheme].bgSecondary} p-4 rounded-lg`}>
            <h3 className={`text-lg font-bold mb-2 ${themes[currentTheme].textPrimary}`}>Filtros</h3>
            <div className="flex flex-wrap gap-4">
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Mes</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                >
                  <option value="">Todos los meses</option>
                  <option value="1">Enero</option>
                  <option value="2">Febrero</option>
                  <option value="3">Marzo</option>
                  <option value="4">Abril</option>
                  <option value="5">Mayo</option>
                  <option value="6">Junio</option>
                  <option value="7">Julio</option>
                  <option value="8">Agosto</option>
                  <option value="9">Septiembre</option>
                  <option value="10">Octubre</option>
                  <option value="11">Noviembre</option>
                  <option value="12">Diciembre</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Año</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                >
                  <option value="">Todos los años</option>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex-1 min-w-[300px]">
          {(() => {
            const stats = calculateSalesStats(services, clients, getServiceName, getClientName);
            return (
              <div className={`${themes[currentTheme].bgSecondary} p-4 rounded-lg`}>
                <h3 className={`text-lg font-bold mb-2 ${themes[currentTheme].textPrimary}`}>Estadísticas de Ventas</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-green-900 bg-opacity-30 p-3 rounded-lg">
                    <p className="text-xs text-gray-400">Total ventas</p>
                    <p className="text-xl font-bold text-green-400">{stats.totalSales}</p>
                  </div>
                  <div className="bg-blue-900 bg-opacity-30 p-3 rounded-lg">
                    <p className="text-xs text-gray-400">Ingresos</p>
                    <div className="flex items-center">
                      <p className="text-xl font-bold text-blue-400">{formatCurrency(stats.totalIncome)}</p>
                      <TrendingUp className="ml-2 text-green-400" size={16} />
                    </div>
                  </div>
                  <div className="bg-purple-900 bg-opacity-30 p-3 rounded-lg">
                    <p className="text-xs text-gray-400">Ticket promedio</p>
                    <div className="flex items-center">
                      <p className="text-xl font-bold text-purple-400">{formatCurrency(stats.avgTicket)}</p>
                      <TrendingUp className="ml-2 text-green-400" size={16} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      
      {movements.length === 0 ? (
        <div className={`${themes[currentTheme].bgSecondary} p-8 rounded-lg text-center`}>
          <p className={themes[currentTheme].textMuted}>No hay movimientos registrados.</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-600 rounded-md text-white"
            onClick={() => {
              setNewMovement({ 
                date: new Date().toISOString().slice(0, 10), 
                type: 'venta', 
                amount: 0, 
                clientId: clients.length > 0 ? clients[0].id : '', 
                serviceId: services.length > 0 ? services[0].id : '', 
                description: '' 
              });
              setShowMovementForm(true);
            }}
          >
            Crear el primer movimiento
          </button>
        </div>
      ) : (
        <div className={`${themes[currentTheme].bgSecondary} rounded-lg overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-800">
              <thead className={themes[currentTheme].bgPrimary}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>#</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Fecha</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Tipo</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Monto</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Cliente</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Servicio</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Descripción</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Acciones</th>
                </tr>
              </thead>
              <tbody className={`${themes[currentTheme].bgSecondary} divide-y divide-gray-800`}>
                {getFilteredMovements().map((movement, index) => (
                  <tr key={movement.id}>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{index + 1}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>
                      {new Date(movement.date).toLocaleDateString()}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{movement.type}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{formatCurrency(movement.amount)}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>
                      {getClientName(movement.client_id || movement.clientId)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>
                      {getServiceName(movement.service_id || movement.serviceId)}
                    </td>
                    <td className={`px-6 py-4 text-sm ${themes[currentTheme].textSecondary}`}>
                      <div className="max-w-xs break-words">{movement.description}</div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary} flex gap-2`}>
                      <button 
                        className="text-blue-400 hover:text-blue-500"
                        onClick={() => handleEditMovement(movement)}
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="text-red-400 hover:text-red-500"
                        onClick={() => handleDeleteMovement(movement.id)}
                      >
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {showMovementForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-md`}>
            <h2 className={`text-xl font-bold mb-4 ${themes[currentTheme].textPrimary}`}>
              {newMovement.id ? 'Editar Movimiento' : 'Nuevo Movimiento'}
            </h2>
            <form className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Tipo</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newMovement.type}
                  onChange={(e) => setNewMovement({ ...newMovement, type: e.target.value })}
                >
                  <option value="venta">Venta</option>
                  <option value="renovacion">Renovación</option>
                  <option value="cancelacion">Cancelación</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Fecha</label>
                <input 
                  type="date" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newMovement.date}
                  onChange={(e) => setNewMovement({ ...newMovement, date: e.target.value })}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Monto</label>
                <input 
                  type="number" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`} 
                  placeholder="0" 
                  value={newMovement.amount}
                  onChange={(e) => setNewMovement({ ...newMovement, amount: e.target.value })}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Cliente</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newMovement.clientId}
                  onChange={(e) => setNewMovement({ ...newMovement, clientId: e.target.value })}
                >
                  {clients.length === 0 ? (
                    <option value="">No hay clientes disponibles</option>
                  ) : (
                    clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Servicio</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newMovement.serviceId}
                  onChange={(e) => setNewMovement({ ...newMovement, serviceId: e.target.value })}
                >
                  {services.length === 0 ? (
                    <option value="">No hay servicios disponibles</option>
                  ) : (
                    services.map(service => (
                      <option key={service.id} value={service.id}>{service.name}</option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Descripción</label>
                <textarea 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`} 
                  rows="3"
                  value={newMovement.description}
                  onChange={(e) => setNewMovement({ ...newMovement, description: e.target.value })}
                ></textarea>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  className={`px-4 py-2 border ${themes[currentTheme].borderColor} rounded-md ${themes[currentTheme].textSecondary}`}
                  onClick={() => {
                    setShowMovementForm(false);
                    if (newMovement.id) {
                      setNewMovement({ date: new Date().toISOString().slice(0, 10), type: 'venta', amount: 0, clientId: '', serviceId: '', description: '' });
                    }
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-blue-600 rounded-md text-white"
                  onClick={handleSaveMovement}
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovementsSection;
